import React from 'react'

import { HelpOutline } from '@mui/icons-material'
import { Box, Card, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { borderRadiusDefault } from '../../../../themes/theme'

type StatValueCardSize = 'default' | 'compact' | 'dense'
type StatValueCardBackgroundIconPlacement = 'offset' | 'contained'

interface StatValueCardProps {
  label: string
  value?: string
  subtext?: string
  accentColor: string
  backgroundIcon?: React.ReactNode
  /**
   * How the faded background icon sits in the card corner. `offset` (default)
   * bleeds past the right edge; `contained` keeps the full icon inside the card.
   */
  backgroundIconPlacement?: StatValueCardBackgroundIconPlacement
  size?: StatValueCardSize
  /**
   * Optional hover-info copy. When set, an info icon next to the label reveals
   * this text on hover/focus (explains how the stat is calculated).
   */
  labelTooltip?: string
  /**
   * Optional rich content. When provided, replaces the default `value` + `subtext` section,
   * keeping the same card shell (label, accent border, faded background icon).
   */
  children?: React.ReactNode
  /**
   * Makes the whole card a button (pointer, hover lift, keyboard focus, Enter / Space).
   * The card's text content is its accessible name.
   */
  onClick?: () => void
}

export const StatValueCard: React.FC<StatValueCardProps> = React.memo(
  ({ label, value, subtext, accentColor, backgroundIcon, backgroundIconPlacement = 'offset', size = 'default', labelTooltip, children, onClick }) => {
    const isCompact = size === 'compact'
    const isDense = size === 'dense'
    const isContainedIcon = backgroundIconPlacement === 'contained'
    const iconIdleOpacity = isContainedIcon ? 0.1 : 0.13
    const iconHoverOpacity = isContainedIcon ? 0.34 : 0.4
    // Both compact and dense stretch to fill a grid/flex cell; dense is a tighter
    // variant for narrow multi-column rows where the compact type wraps.
    const isFluid = isCompact || isDense

    const buttonProps = onClick
      ? {
          role: 'button',
          tabIndex: 0,
          onClick,
          onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.target !== event.currentTarget) return
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onClick()
            }
          },
        }
      : {}

    return (
      <Card
        elevation={1}
        {...buttonProps}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: isDense ? 100 : isCompact ? 92 : 82,
          borderRadius: borderRadiusDefault,
          border: `1px solid ${alpha(accentColor, 0.14)}`,
          px: 2,
          py: isDense ? 1.5 : 1.2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          '&:hover .StatValueCard-backgroundIcon': {
            color: alpha(accentColor, iconHoverOpacity),
          },
          ...(isFluid && {
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            boxSizing: 'border-box',
          }),
          ...(onClick && {
            cursor: 'pointer',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              borderColor: alpha(accentColor, 0.4),
              boxShadow: 3,
            },
            '&:focus-visible': {
              outline: `2px solid ${alpha(accentColor, 0.5)}`,
              outlineOffset: 2,
            },
          }),
        }}
      >
        {backgroundIcon != null && (
          <Box
            className="StatValueCard-backgroundIcon"
            sx={{
              position: 'absolute',
              right: isContainedIcon ? (isDense ? 4 : 8) : -12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: alpha(accentColor, iconIdleOpacity),
              pointerEvents: 'none',
              transition: 'color 0.25s ease',
              '& svg': {
                fontSize: isContainedIcon ? (isDense ? 36 : isCompact ? 44 : 52) : isDense ? 44 : isCompact ? 56 : 64,
              },
            }}
          >
            {backgroundIcon}
          </Box>
        )}

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, lineHeight: 1 }}>
          <Typography
            component="span"
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'text.secondary',
              fontWeight: 'bold',
              fontSize: isDense ? '0.72rem' : '0.82rem',
              lineHeight: 1,
            }}
          >
            {label}
          </Typography>
          {labelTooltip ? (
            <GRTooltip
              content={labelTooltip}
              styles={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', lineHeight: 0, flexShrink: 0, marginLeft: 2 }}
            >
              <HelpOutline
                sx={{
                  display: 'block',
                  fontSize: isDense ? '0.72rem' : '0.82rem',
                  color: 'text.disabled',
                }}
              />
            </GRTooltip>
          ) : null}
        </Box>

        {children != null ? (
          <Box sx={{ position: 'relative', zIndex: 1, mt: isDense ? 0.75 : 0.6, minWidth: 0 }}>{children}</Box>
        ) : (
          <>
            {value != null && (
              <Typography
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  mt: 0.35,
                  fontWeight: 'bold',
                  fontSize: isDense ? { xs: '1.5rem', md: '1.65rem' } : isCompact ? { xs: '1.5rem', md: '1.65rem' } : { xs: '2.9rem', md: '3.1rem' },
                  lineHeight: isFluid ? 1.05 : 1,
                  color: accentColor,
                  fontVariantNumeric: 'tabular-nums',
                  ...(isFluid && { overflowWrap: 'break-word' }),
                }}
              >
                {value}
              </Typography>
            )}
            {subtext != null && subtext !== '' && (
              <Typography
                variant="caption"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  mt: 0.4,
                  display: 'block',
                  color: 'text.secondary',
                  lineHeight: 1.3,
                }}
              >
                {subtext}
              </Typography>
            )}
          </>
        )}
      </Card>
    )
  }
)

StatValueCard.displayName = 'StatValueCard'
